import { useContext, useRef, useState } from 'react';

import { Box, Grid, GridItem } from '@chakra-ui/react';
import { Button, Card, CardContent, Goal } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';
import { ProfileContext } from 'Deer/pages/Profile/hooks/ProfileContext';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { useOKROwnership } from 'reducers/okrsPageSlice';
import { AgGridAddNewGoalObjectiveSplitButton } from 'screens/Team/Rabbit/Goals/AgGridAddNewGoalObjectiveSplitButton';
import { usePersistenceKey } from 'screens/Team/Rabbit/Goals/AgGridTreeView/GoalsTreeViewTable';
import {
  goalsCascadedViewReactQueryKey,
  useGoalsCascadedViewData,
} from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';
import { checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import noListData from 'assets/assetsV1/illustrations/deerIllustrations/noListData.svg';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendCreatedKREvent, sendCreatedObjectiveEvent } from 'constants/mixpanelHelper';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { GoalsDonutChart } from './GoalsDonutChart';
import { GoalsTable } from './GoalsTable';
import { calculateProgressPercentages } from './helper';

// import { calculateGoalsWithOrWithoutCheckIns, getGoalStatus } from './helper';

const COLOR_MAP = {
  ON_TRACK: '#B1E39F',
  OFF_TRACK: '#FFDE88',
  AT_RISK: '#FFE1D9',
  CLOSED: '#CCCCCC',
  CLOSED_ACHIEVED: '#DBF2D3',
  CLOSED_DROPPED: '#EAEAEA',
  CLOSED_NOT_ACHIEVED: '#FFE1D9',
};

const TEXT_MAP = {
  ON_TRACK: 'On-track',
  OFF_TRACK: 'Off-track',
  AT_RISK: 'At-risk',
  CLOSED: 'Closed',
  CLOSED_ACHIEVED: 'Closed & achieved',
  CLOSED_DROPPED: 'Closed & dropped',
  CLOSED_NOT_ACHIEVED: 'Closed & not achieved',
};

const TEXT_COLOR_MAP = {
  ON_TRACK: '#44812F',
  OFF_TRACK: '#B96400',
  AT_RISK: '#C62C02',
  CLOSED: '#333333',
  CLOSED_ACHIEVED: '#1A5724',
  CLOSED_DROPPED: '#666666',
  CLOSED_NOT_ACHIEVED: '#A12402',
};

const EmptyGoalScreenComponent = ({ onCreateGoal }) => (
  <Card style={{ height: '100%' }}>
    <CardContent>
      <EmptyScreen
        illustrationSrc={noListData}
        heading="No goals added"
        isSmallTextSize
        description="Create, own or collaborate on goals to unlock valuable insights on your performance"
        // eslint-disable-next-line react/no-unstable-nested-components
        CTAComp={() => (
          <Button iconLeft={Goal} onClick={onCreateGoal} variant="outline">
            Add New Goal
          </Button>
        )}
      />
    </CardContent>
  </Card>
);

export const GoalsOverview = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const gridRef = useRef();
  const employeeContext = useContext(ProfileContext);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const { data: timelineData } = useTimeBoundTimePeriods();
  const [cardKPIData, setCardKPIData] = useState([]);
  const queryClient = useQueryClient();
  const { moduleName } = useOrganisationSettings();
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const okrDetailsForSelectedTimeLine = useSelector(okrCreationPanelStateSelector);
  const targetMetricCardTablePersistenceKey = usePersistenceKey({ viewId: '-12' });

  const { timeBoundDetails } = okrDetailsForSelectedTimeLine;

  const currentURL = history.location.pathname;

  // Generate the key using getUniqueCardKey function
  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myDataGoalsOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'goalsOverview',
    section: 'myData',
  });

  const ownership = useOKROwnership();

  const { data: goalsData, isLoading: isGoalsDataLoading } = useGoalsCascadedViewData({
    attributeFilter: {
      filterConditions: [
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [employeeContext?.id ? employeeContext?.id : loggedInEmployee?.id],
        },
        {
          type: 'TYPE',
          operatorType: 'IN',
          data: ['GOAL'],
        },
      ],
    },
    // Other query parameters
    cohort: {
      id: -12,
    },
    groupByKeys: {
      uuid: null,
      keyName: 'objective',
      displayName: 'Objective',
      enabled: true,
    },
    splitByKeys: {
      uuid: null,
      keyName: 'none',
      displayName: 'None',
      enabled: true,
    },
    orderBy: null,
    timeBounds: timelineData?.current ? [timelineData?.current] : [null],
    isHierarchyView: false,
    ownership,
    pageType: employeeContext?.id ? 'PROFILE_PAGE' : 'TEAM_HUB',
  });

  // Function to trigger a refetch of goals data
  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        attributeFilter: {
          filterConditions: [
            {
              type: 'NAME',
              operatorType: 'IN',
              data: [employeeContext?.id ? employeeContext?.id : loggedInEmployee?.id],
            },
            {
              type: 'TYPE',
              operatorType: 'IN',
              data: ['GOAL'],
            },
          ],
        },
        cohort: {
          id: -12,
        },
        groupByKeys: {
          uuid: null,
          keyName: 'objective',
          displayName: 'Objective',
          enabled: true,
        },
        splitByKeys: {
          uuid: null,
          keyName: 'none',
          displayName: 'None',
          enabled: true,
        },
        orderBy: null,
        timeBounds: timelineData?.current ? [timelineData?.current] : [null],
        isHierarchyView: false,
        pageType: 'TEAM_HUB',
      })
    );
  };

  // Calculating progress percentages for the donut chart
  const progressCountsForChart = goalsData && calculateProgressPercentages(goalsData);

  // Mapping progress data for the donut chart
  const donutChartData =
    goalsData &&
    progressCountsForChart?.map((data) => ({
      label: TEXT_MAP[data.status],
      labelColor: TEXT_COLOR_MAP[data.status],
      value: data.percentage,
      color: COLOR_MAP[data.status],
    }));

  // Calculating total goals with or without check-ins
  // const totalGoalsWithOrWithoutCheckIns = goalsData && calculateGoalsWithOrWithoutCheckIns(goalsData);

  // Checking overall goal status based on check-ins and progress counts
  // const checkGoalStatus =
  //   totalGoalsWithOrWithoutCheckIns &&
  //   getGoalStatus(
  //     totalGoalsWithOrWithoutCheckIns?.progressCounts?.AT_RISK,
  //     totalGoalsWithOrWithoutCheckIns?.checkInCount,
  //     moduleName
  //   );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAddKR = () => {
    dispatch(
      // @ts-expect-error error (^.^)
      openCreateOKRPanel({
        selectedObjective: null,
        selectedTimeBoundPeriod: timeBoundDetails,
        okrPanelType: OKR_TYPES.KEY_RESULT,
        ownerEmployee: employeeContext?.id ? employeeContext : null,
        onKeyResultCreation(okrDetails) {
          sendCreatedKREvent({
            eventProperties: {
              source: 'Add New Button',
              page: 'TEAM_HUB',
              goalId: okrDetails?.uuid,
            },
          });
          refetchCascadedViewData();
        },
        onObjectiveCreation(okrDetails) {
          sendCreatedObjectiveEvent({
            eventProperties: {
              source: 'Add New Button',
              page: 'TEAM_HUB',
              objectiveId: okrDetails?.uuid,
              description: okrDetails?.description,
              level: okrDetails?.objectiveLevel,
              type: okrDetails?.type,
              timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
            },
          });
          refetchCascadedViewData();
        },
        history,
        location,
      })
    );
  };

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'goalsOverview',
        section: 'myData',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  if (isGoalsDataLoading) {
    return <SkeletonLoader />;
  }

  return (
    // hardcoding subTitle and subTitleColor as of now as per the requirements from sakshi and abi
    <OverviewSection
      open={isCardOpen}
      onOpenChange={handleChange}
      subTitle="Your goal progress is currently averaging at 60%, despite having 2 goals at risk. Maven suggests considering setting stretch targets for the majority of your goals to push yourself further. Let's aim high together!"
      subTitleColor="success"
      showCaption={goalsData?.length > 0 && checkNewStaticDataFeaturesEnabled() && !employeeContext}
      rightComp={
        <AgGridAddNewGoalObjectiveSplitButton
          variant="outline"
          onAddKRClick={() => {
            dispatch(
              // @ts-expect-error error (^.^)
              openCreateOKRPanel({
                selectedObjective: null,
                selectedTimeBoundPeriod: timeBoundDetails,
                okrPanelType: OKR_TYPES.KEY_RESULT,
                ownerEmployee: employeeContext?.id ? employeeContext : null,
                onKeyResultCreation(okrDetails) {
                  sendCreatedKREvent({
                    eventProperties: {
                      source: 'Add New Button',
                      page: 'TEAM_HUB',
                      goalId: okrDetails?.uuid,
                    },
                  });
                  refetchCascadedViewData();
                },
                onObjectiveCreation(okrDetails) {
                  sendCreatedObjectiveEvent({
                    eventProperties: {
                      source: 'Add New Button',
                      page: 'TEAM_HUB',
                      objectiveId: okrDetails?.uuid,
                      description: okrDetails?.description,
                      level: okrDetails?.objectiveLevel,
                      type: okrDetails?.type,
                      timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                    },
                  });
                  refetchCascadedViewData();
                },
                history,
                location,
              })
            );
          }}
          onAddObjectiveClick={() => {
            dispatch(
              // @ts-expect-error error (^.^)
              openCreateOKRPanel({
                selectedObjective: null,
                selectedTimeBoundPeriod: timeBoundDetails,
                okrPanelType: OKR_TYPES.OBJECTIVE,
                ownerEmployee: employeeContext?.id ? employeeContext : null,
                onKeyResultCreation(okrDetails) {
                  sendCreatedKREvent({
                    eventProperties: {
                      source: 'Add New Button',
                      page: 'TEAM_HUB',
                      goalId: okrDetails?.uuid,
                    },
                  });
                  refetchCascadedViewData();
                },
                onObjectiveCreation(okrDetails) {
                  sendCreatedObjectiveEvent({
                    eventProperties: {
                      source: 'Add New Button',
                      page: 'TEAM_HUB',
                      objectiveId: okrDetails?.uuid,
                      description: okrDetails?.description,
                      level: okrDetails?.objectiveLevel,
                      type: okrDetails?.type,
                      timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                    },
                  });
                  refetchCascadedViewData();
                },
                history,
                location,
              })
            );
          }}
        />
      }
      title={`${employeeContext?.isVisitingOwnProfile ? 'My' : ''} ${modifyString(
        moduleName,
        employeeContext?.isVisitingOwnProfile ? StringModifier.SENTENCE_CASE : StringModifier.TITLE_CASE
      )}s`}
    >
      {!goalsData?.length ? (
        <EmptyGoalScreenComponent onCreateGoal={handleAddKR} />
      ) : (
        <Grid width="full" templateColumns={{ base: '1fr', md: '2fr 3fr' }} gap="8px" minHeight="467px">
          <GridItem>
            <Box height="full">
              <GoalsDonutChart chartData={donutChartData} />
            </Box>
          </GridItem>
          <GridItem rowSpan={2}>
            <GoalsTable
              refetchCascadedViewData={refetchCascadedViewData}
              data={goalsData}
              setCardKPIData={setCardKPIData}
              gridRef={gridRef}
            />
          </GridItem>
          <GridItem>
            <Box height="full">
              <MetricCards
                height="100%"
                columns="repeat(2, 1fr)"
                cardTitle="Metrics"
                cards={cardKPIData}
                module={MODULE_KEYS_METRIC_CARDS_DEER.GOAL}
                gridStatePersistenceKey={targetMetricCardTablePersistenceKey}
                isOverviewPageCard
              />
            </Box>
          </GridItem>
        </Grid>
      )}
    </OverviewSection>
  );
};
